import React from "react";

import PropTypes from "prop-types";
import clsx from "clsx";
import { lighten, makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableFooter,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  Checkbox,
  Tooltip,
  Box,
  IconButton,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import Collapse from "@material-ui/core/Collapse";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import "./.TableDefault.css";

import { CustomTable, CustomContainer } from "./styles/TableDefault";
import { useEffect } from "react";

function descendingComparator(a, b, orderBy) {
  // Converte para número (se possível), antes de ordenar
  let valor1 = Number.isNaN(Number(a[orderBy]))
    ? a[orderBy]
    : Number(a[orderBy]);

  valor1 = a[orderBy] === null ? "" : valor1;

  let valor2 = Number.isNaN(Number(b[orderBy]))
    ? b[orderBy]
    : Number(b[orderBy]);

  valor2 = b[orderBy] === null ? "" : valor2;

  if (valor2 < valor1) {
    return -1;
  } else if (valor2 > valor1) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    columns,
    selectable,
    subitemTable,
    multiSelect,
    disabledTab,
    disablePaddingCheckbox,
    customDisableCheckAll,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {selectable && (
          <TableCell padding={disablePaddingCheckbox ? "none" : "checkbox"}>
            {multiSelect && (
              <Checkbox
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{ "aria-label": "Selecione todos os itens" }}
                disabled={!multiSelect || customDisableCheckAll}
              />
            )}
          </TableCell>
        )}
        {subitemTable && <TableCell></TableCell>}
        {columns.map((headCell, index) => {
          return (
            <TableCell
              key={headCell.id ?? index}
              align={headCell.align}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
              style={headCell.style || {}}
            >
              <Tooltip
                title={headCell.description ?? ""}
                aria-label={headCell.description ?? ""}
                tabIndex={disabledTab ? "-1" : "0"}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={
                    !headCell.disabled
                      ? createSortHandler(headCell.id)
                      : () => {}
                  }
                  hideSortIcon={true}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </Tooltip>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography
          className={classes.title}
          color="inherit"
          variant="h6"
          component="div"
        >
          {numSelected > 1
            ? `${numSelected} itens selecionados`
            : `${numSelected} item selecionado`}
        </Typography>
      ) : (
        <Typography
          className={classes.title}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {props.tableTitle}
        </Typography>
      )}

      {/*
        Caso queira adicionar um filtro ou exclusão direto na tabela
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton aria-label="filter list">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )} */}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

export default function TableDefault({
  disabledEmptySpace = false,
  paddingNoneRows = false,
  disablePaddingCheckbox = false,
  disabledDefaultPagination = false,
  stickyHeader = true,
  disabledTab = false,
  maxHeight = "",
  minHeight = "",
  subitemTable = false,
  columns = [],
  values = [],
  rowsPerPageOptions = [],
  rowsPerPage = 10,
  rowId = "id",
  tableTitle = "",
  selectable = false,
  selected = [],
  loading = false,
  quantidadeItensTotal,
  customHandleChangePage = () => {
    return [];
  },
  // Para cada página, chama o customHandleChancePage (Não deseja utilizar o cache)
  customPagination = false,
  setSelected = () => {},
  setInitialPage = () => {},
  initialPage = 0,
  initialSize = 33,
  multiSelect = true,
  customDisableCheckAll = false,
  pageProp,
  setPageProp,
  smallFont = false,
  selectedByRow = true,
  sortBy = null,
  orderSort = "asc",
  allChecked = false,
  setAllChecked = () => {},
  doubleTap = () => {},
  footerData = null,
  ...props
}) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(-1);
  const [order, setOrder] = React.useState(orderSort);
  const [orderBy, setOrderBy] = React.useState(sortBy || rowId);
  const [page, setPage] = React.useState(initialPage);
  const [rowsPerPageDefault, setRowsPerPageDefault] =
    React.useState(rowsPerPage);

  useEffect(() => {
    setAllChecked(allChecked);
    if (loading || !allChecked) return;

    setSelected(values.filter((n) => !n.notSelectable));
    // eslint-disable-next-line
  }, [allChecked, loading]);

  // Alertas para o desenvolvedor
  if (!columns || columns.length === 0) {
    alert("É necessário informar a propriedade columns no Grid");
    return <></>;
  } else if (selectable && !setSelected && !selected) {
    alert(
      "É necessário informar as propriedades selected e setSelected, quando o grid for selecionável"
    );
    return <></>;
  }

  let columnIds = [];
  let rowFormat = [];
  let rowToolTip = [];
  let rowAlign = [];
  // Adiciona os campos padrões nas colunas (caso não tenham sido preenchidos)
  columns.forEach((coluna) => {
    // Padrão de alinhamento para a esquerda (caso não tenha sido definido - Numérico para a direita)
    if (!coluna.align) coluna.align = coluna.numeric ? "right" : "left";
    rowAlign.push(coluna.align);
    // Formatação de dígitos
    rowFormat.push(coluna.format);
    rowToolTip.push(coluna.tooltip);
    coluna.disablePadding = coluna.disablePadding
      ? coluna.disablePadding
      : false;
    coluna.id = coluna.id ? coluna.id : coluna.label;
    columnIds.push(coluna.id);
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Adiciona todos os objetos dentro do selected
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = values.filter((n) => !n.notSelectable);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }

    setAllChecked(event.target.checked);
    return null;
  };

  const handleClicks = (event, item) => {
    switch (event.detail) {
      case 2:
        handleClick(event, item);
        doubleTap();
        break;
      default:
        handleClick(event, item);
        break;
    }
  };

  // Adiciona/remove o selected de acordo com o rowId identificado
  const handleClick = (event, item) => {
    if (!selectable) return;

    if (item.notSelectable) return;

    if (!multiSelect) {
      setSelected([item]);
      setAllChecked(false);
      return;
    }

    let selectedIndex = -1;

    // Verifica o index do item, caso não encontre, manda o -1 (adiciona um item novo)
    selected.forEach((select, index) => {
      if (select[rowId] === item[rowId]) {
        selectedIndex = index;
        return;
      }
    });

    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
    setAllChecked(newSelected.length === values.length);
  };

  const handleChangePage = async (event, newPage) => {
    // Caso exista o item carregado no values, não chama o customHandleChangePage
    // (Ou deseja fazer um custom - Sem cache)
    if (!values || values.length <= newPage * rowsPerPage || customPagination) {
      // Sequencia do envio: event, página da mudança, primeiro item da página atual (length)
      let retorno = await customHandleChangePage(
        event,
        newPage,
        newPage * rowsPerPage + 1
      );
      if (!Array.isArray(retorno)) {
        alert(
          "Ops, é necessário que o método customHandleChangePage retorne um array"
        );
        return;
      }
      retorno.forEach((valor) => {
        values.push(valor);
      });
    }
    if (setPageProp) setPageProp(newPage);
    else setPage(newPage);

    setInitialPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPageDefault(parseInt(event.target.value, 10));
    if (setPageProp) setPageProp(0);
    else setPage(0);
    setInitialPage(0);
  };

  const isSelected = (item) => {
    let retorno = false;
    selected.forEach((select) => {
      if (select[rowId] === item[rowId]) {
        retorno = true;
      }
    });
    return retorno;
  };

  // Calculo para saber quantos itens estão mostrando no momento
  let qtdPaginaAtual =
    values.length -
    (Number.isInteger(pageProp) ? pageProp : page) * rowsPerPageDefault;

  if (customPagination) qtdPaginaAtual = values.length;

  const emptyRows =
    rowsPerPageDefault - Math.min(rowsPerPageDefault, qtdPaginaAtual);

  return (
    <CustomTable {...props}>
      <Paper className={classes.paper}>
        {tableTitle && (
          <EnhancedTableToolbar
            numSelected={selected.length}
            tableTitle={tableTitle}
          />
        )}
        <CustomContainer
          style={{ maxHeight: `${maxHeight}`, minHeight: `${minHeight}` }}
          className={smallFont ? "smallFont" : ""}
        >
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={"small"}
            aria-label="enhanced table"
            stickyHeader={stickyHeader}
          >
            <EnhancedTableHead
              columns={columns}
              align={rowAlign}
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              selectable={selectable}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={values.length}
              subitemTable={subitemTable}
              multiSelect={multiSelect}
              customDisableCheckAll={customDisableCheckAll}
              disabledTab={disabledTab}
              disablePaddingCheckbox={disablePaddingCheckbox}
              selectedByRow={selectedByRow}
            />
            <TableBody>
              {
                // Adiciona o loading, caso seja necessário
                loading
                  ? (function loading() {
                      let minRows = [];
                      for (let row = 0; row < rowsPerPageDefault; row++) {
                        minRows.push(
                          <TableRow
                            role="checkbox"
                            tabIndex={-1}
                            key={`TableRowLoading_${row}`}
                          >
                            {selectable && (
                              <TableCell key={`skeleton_${row}`}>
                                <Skeleton variant="rect" animation="wave" />
                              </TableCell>
                            )}
                            {columnIds.map((value, index) => {
                              return (
                                <TableCell key={`skeleton_${row}_${index}`}>
                                  <Skeleton variant="text" animation="wave" />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      }
                      return minRows;
                    })()
                  : // Adiciona as linhas da tabela, depois de ficar carregado
                    stableSort(values, getComparator(order, orderBy))
                      // Se for customPagination, a pesquisa deve ser feita fora da table,
                      // e sempre será retornado a quantidade de itens aqui.
                      // Se não for customPagination, dá uma slice para retornar somente a quantidade definida.
                      .slice(
                        customPagination
                          ? 0
                          : (Number.isInteger(pageProp) ? pageProp : page) *
                              rowsPerPageDefault,
                        (!disabledDefaultPagination &&
                          (customPagination
                            ? rowsPerPageDefault
                            : (Number.isInteger(pageProp) ? pageProp : page) *
                                rowsPerPageDefault +
                              rowsPerPageDefault)) ||
                          values.length
                      )
                      .map((row, index) => {
                        const isItemSelected = isSelected(row);
                        const labelId = `enhanced-table-checkbox-${index}`;
                        // Adiciona os itens da tabela
                        return (
                          <React.Fragment key={index}>
                            <TableRow
                              style={{
                                // Remove o background em caso de item selecionado
                                backgroundColor: isItemSelected
                                  ? (row["rowError"] && "#ffcfc7") || "white"
                                  : row["backgroundColor"] ||
                                    (row["rowError"] && "#ffcfc7"),
                                ...(row["notSelectable"] && {
                                  pointerEvents: "none",
                                }),
                              }}
                              className={
                                row?.alternativeCheck ? "altCheck" : ""
                              }
                              hover
                              onClick={(event) => {
                                if (selectedByRow) handleClicks(event, row);
                              }}
                              role="checkbox"
                              aria-checked={isItemSelected}
                              tabIndex={-1}
                              selected={isItemSelected}
                            >
                              {subitemTable && (
                                <TableCell style={{ width: 20 }}>
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() =>
                                      setOpen(index === open ? -1 : index)
                                    }
                                  >
                                    {index === open ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                </TableCell>
                              )}

                              {selectable && (
                                <TableCell
                                  padding={
                                    paddingNoneRows ? "none" : "checkbox"
                                  }
                                  key={`TableCell_selectable_${row[rowId]}`}
                                >
                                  {multiSelect && (
                                    <Checkbox
                                      key={`checkbox_${row[rowId]}`}
                                      checked={isItemSelected}
                                      inputProps={{
                                        "aria-labelledby": labelId,
                                      }}
                                      onChange={(event) => {
                                        if (!selectedByRow)
                                          handleClicks(event, row);
                                      }}
                                    />
                                  )}
                                </TableCell>
                              )}
                              {columnIds.map((id, indexColumns) => {
                                return rowToolTip[indexColumns] ? (
                                  <Tooltip
                                    title={rowToolTip[indexColumns](row)}
                                  >
                                    <TableCell
                                      align={rowAlign[indexColumns]}
                                      key={indexColumns}
                                      padding={
                                        paddingNoneRows ? "none" : "normal"
                                      }
                                      fontSize={"1.8vh"}
                                    >
                                      {
                                        // Adiciona a formatação
                                        rowFormat[indexColumns] && row
                                          ? rowFormat[indexColumns](row)
                                          : row[id]
                                      }
                                    </TableCell>
                                  </Tooltip>
                                ) : (
                                  <TableCell
                                    align={rowAlign[indexColumns]}
                                    key={indexColumns}
                                    padding={
                                      paddingNoneRows ? "none" : "normal"
                                    }
                                    fontSize={"1.8vh"}
                                  >
                                    {
                                      // Adiciona a formatação
                                      rowFormat[indexColumns] && row
                                        ? rowFormat[indexColumns](row)
                                        : row[id]
                                    }
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                            <TableRow>
                              <TableCell
                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                colSpan={12}
                              >
                                <Collapse
                                  in={index === open}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box margin={1}>{row.subitemTable}</Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
              }
              {/* adiciona o espaço das rows vazias */}
              {!disabledEmptySpace && emptyRows > 0 && !loading && (
                <TableRow
                  style={{
                    height: (initialSize * emptyRows) / 2,
                  }}
                >
                  <TableCell colSpan={12} />
                </TableRow>
              )}
            </TableBody>
            {footerData && (
              <TableFooter>
                {footerData.map((footerRow, footerIndex) => (
                  <TableRow key={`footer_row_${footerIndex}`}>
                    {selectable && (
                      <TableCell
                        padding={disablePaddingCheckbox ? "none" : "checkbox"}
                      />
                    )}
                    {subitemTable && <TableCell></TableCell>}
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={`footer_cell_${footerIndex}_${colIndex}`}
                        align={column.align || "left"}
                        padding={column.disablePadding ? "none" : "normal"}
                        style={{
                          fontWeight: "bold",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        {footerRow[column.id || column.label] || ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableFooter>
            )}
          </Table>
        </CustomContainer>
        {disabledDefaultPagination ? (
          <> </>
        ) : (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={quantidadeItensTotal ?? values.length}
            rowsPerPage={rowsPerPageDefault}
            page={Number.isInteger(pageProp) ? pageProp : page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            nextIconButtonProps={
              loading
                ? {
                    disabled: loading,
                  }
                : undefined
            }
          />
        )}
      </Paper>
    </CustomTable>
  );
}
